<script setup lang="ts">
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { generateHTML } from '@tiptap/html'
import { type FileReference, translate, type TranslatedNews } from '~/composables/charon'
import { bodyExtensions, titleExtensions } from '~/tiptap/config'

const { charon } = useCharon()
const { d, t, locale } = useI18n()

const props = defineProps<{
  content: TranslatedNews
}>()

const { data: reference } = useAsyncData(
  `file-reference-${props.content.image ?? 'placeholder'}`,
  async () => {
    if (!props.content.image) {
      return null
    }
    return charon<FileReference>(`/file-references/${props.content.image}`)
  },
  {
    lazy: true,
    server: true,
    watch: [() => props.content.image],
  },
)

// rendered as static HTML so that the title is part of the SSR response
const titleHtml = computed(() =>
  props.content.title
    ? generateHTML(props.content.title, titleExtensions({ customHeadingLevel: 1 }))
    : '',
)

const bodyEditor = useEditor({
  extensions: bodyExtensions(),
  content: props.content.body,
  editable: false,
})
</script>

<template>
  <div class="flex flex-col gap-6 text-gray-900">
    <div class="rounded-md shadow-lg border border-gray-200 bg-white flex flex-col">
      <NewsBanner
        :image="props.content.image"
        :alt="reference ? translate(reference.locales, locale)?.description : ''"
      />

      <span
        class="bg-primary h-full w-full
          min-h-[10px] min-w-[10px] laptop:min-h-[15px] laptop:min-w-[15px]"
      />

      <div class="flex flex-col gap-4 m-card text-gray-900">
        <div class="editor flex flex-col">
          <div
            class="ProseMirror"
            v-html="titleHtml"
          />
        </div>

        <div class="flex flex-row gap-2 mt-auto">
          <span class="b2 text-slate-700">
            {{ t(`jc.news.type.${content.type}.title`) }}
          </span>
          <span
            v-if="content.publishDate"
            class="b2 text-slate-700"
          >
            {{ d(content.publishDate ?? '', 'short') }}
          </span>
        </div>

        <div class="editor flex flex-col">
          <EditorContent :editor="bodyEditor" />
        </div>
      </div>
    </div>
  </div>
</template>
